// After a user-triggered update reload, verify the running bundle matches the deployed version.
// If not, retry a hard reload (lazy chunks may still be cached otherwise).

import buildGitSha from "../src/buildGitSha.js";
import {
  fetchDeployedVersion,
  setStoredVersion,
} from "./frontendVersionCheck";
import { forceHardReload } from "./forceHardReload";
import {
  canAttemptFrontendReload,
  markFrontendReloadAttempt,
} from "./frontendReloadGuard";

const PENDING_ACK_KEY = "frontend:pendingVersionAck";

export function setPendingVersionAck(version) {
  if (!version) return;
  try {
    sessionStorage.setItem(PENDING_ACK_KEY, String(version));
  } catch {
    // ignore
  }
}

function runningVersion() {
  const version = buildGitSha;
  return typeof version === "string" && version && version !== "unknown" ? version : null;
}

function stripHardReloadQueryParam() {
  try {
    const url = new URL(window.location.href);
    if (!url.searchParams.has("_hr")) return;
    url.searchParams.delete("_hr");
    const next = `${url.pathname}${url.search}${url.hash}`;
    window.history.replaceState(null, "", next);
  } catch {
    // ignore
  }
}

function applyPendingVersionAckIfReady(deployedVersion) {
  try {
    const pending = sessionStorage.getItem(PENDING_ACK_KEY);
    if (!pending) return;
    const running = runningVersion();
    if (running && pending === running && (!deployedVersion || pending === deployedVersion)) {
      setStoredVersion(pending);
      sessionStorage.removeItem(PENDING_ACK_KEY);
    }
  } catch {
    // ignore
  }
}

/** Apply stored ack when the running bundle already matches the pending deploy version. */
export function finalizePendingVersionAck(deployedVersion = null) {
  applyPendingVersionAckIfReady(deployedVersion);
}

function isUserTriggeredReload() {
  try {
    if (sessionStorage.getItem(PENDING_ACK_KEY)) return true;
    return new URLSearchParams(window.location.search).has("_hr");
  } catch {
    return false;
  }
}

/** Call once at bootstrap before React mount. May navigate away (hard reload). */
export async function ensureFreshFrontendBundle() {
  const deployed = await fetchDeployedVersion();
  const running = runningVersion();

  if (!deployed || !running || deployed === running) {
    applyPendingVersionAckIfReady(deployed);
    stripHardReloadQueryParam();
    return;
  }

  if (!isUserTriggeredReload()) {
    return;
  }

  if (!canAttemptFrontendReload()) {
    console.warn(
      "[ensureFreshFrontendBundle] Deployed bundle still stale after reload —",
      { running, deployed },
    );
    stripHardReloadQueryParam();
    return;
  }

  console.warn(
    "[ensureFreshFrontendBundle] Running bundle does not match deploy — hard reload",
    { running, deployed },
  );
  markFrontendReloadAttempt();
  await forceHardReload();
}
